import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/api';

const Loader = () => (
  <div className="flex items-center justify-center">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
    </div>
  </div>
);

const SchemeBooks = () => {
  const [schemeBooks, setSchemeBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(100);

  useEffect(() => {
    fetchSchemeBooks();
  }, [page]);

  const fetchSchemeBooks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/scheme-books`, {
        params: { page, perPage }
      });
      setSchemeBooks(response.data.schemeBooks);
      setTotalPages(response.data.totalPages);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching scheme books:', err);
      setError('Failed to fetch scheme books');
      setLoading(false);
    }
  };

  const searchSchemeBooks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/search-scheme-books`, {
        params: { search: searchQuery }
      });
      if (response.data.schemeBooks.length === 0) {
        setError('Scheme book not found');
      } else {
        setSchemeBooks(response.data.schemeBooks);
        setTotalPages(1);
        setError('');
      }
      setLoading(false);
    } catch (err) {
      console.error('Error searching scheme books:', err);
      setError('Failed to search scheme books');
      setLoading(false);
    }
  };

  const handleDownload = async (fileName) => {
    try {
      const response = await axios.get(`${BASE_URL}/scheme_books/${fileName}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file');
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim() !== '') {
      searchSchemeBooks();
    } else {
      setError('Please enter a search term.');
    }
  };

  const handleResetSearch = () => {
    setSearchQuery('');
    setError('');
    fetchSchemeBooks();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Scheme Books</h1>
            <div className="h-1 w-full bg-gray-200 mt-2"></div>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                placeholder="Search by EmployeeNumber, Description, or Class"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                >
                  Search
                </button>
                <button
                  onClick={handleResetSearch}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          ) : (
            <>
              {/* Scheme Books Table */}
              {schemeBooks.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-pink-600 rounded-t-lg">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tl-lg">Class</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Teacher</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tr-lg">Download</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {schemeBooks.map((schemeBook) => (
                        <tr key={schemeBook.id} className="hover:bg-pink-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{schemeBook.class}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{schemeBook.description}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{schemeBook.Name} {schemeBook.Surname}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(schemeBook.date).toLocaleDateString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleDownload(schemeBook.document)}
                              className="text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                            >
                              Download
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                !error && (
                  <div className="text-center py-12">
                    <p className="text-gray-500 text-lg">No scheme books found.</p>
                  </div>
                )
              )}

              {/* Pagination Controls */}
              {schemeBooks.length > 0 && (
                <div className="flex items-center justify-between mt-6">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchemeBooks;
